import { useState } from 'react';
import type { PermissionRequest } from '@claude-remote/shared';
import { Button } from './Button.js';

interface AskQuestion {
  question: string;
  header?: string;
  options: Array<{ label: string; description?: string }>;
  multiSelect: boolean;
}

interface Props {
  request: PermissionRequest;
  onAnswer: (text: string) => void;
  onDeny: () => void;
}

/**
 * Интерактивная карточка для AskUserQuestion: render радиокнопок/чекбоксов,
 * сбор выбора по каждому вопросу + свободный текст, submit отдаёт собранный
 * ответ в формате `Q: ...\nA: ...` через onAnswer.
 *
 * Используется и на странице сессии, и в /inbox — без неё в /inbox юзер
 * мог только Approve/Deny через ToolInputView'шный read-only-рендер,
 * собственно «ответить на вопрос» там было нельзя.
 */
export function AskUserQuestionCard({ request, onAnswer, onDeny }: Props) {
  const questions = parseAskInput(request.toolInput);
  // selections[i] хранит выбранные label'ы для i-го вопроса.
  const [selections, setSelections] = useState<string[][]>(() =>
    questions.map(() => []),
  );
  const [freeText, setFreeText] = useState<string[]>(() => questions.map(() => ''));
  const [submitting, setSubmitting] = useState(false);

  if (questions.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-600/40 bg-amber-500/10 p-3 space-y-2">
        <div className="text-sm font-medium">🔧 AskUserQuestion</div>
        <div className="text-xs text-amber-100/70">
          Не смог разобрать вопросы — закрой карточку или ответь промптом ниже.
        </div>
        <Button variant="danger" className="w-full" onClick={onDeny}>
          Закрыть
        </Button>
      </div>
    );
  }

  function toggle(qi: number, label: string) {
    setSelections((prev) => {
      const next = prev.map((arr) => [...arr]);
      const cur = next[qi]!;
      const idx = cur.indexOf(label);
      if (questions[qi]!.multiSelect) {
        if (idx >= 0) cur.splice(idx, 1);
        else cur.push(label);
      } else {
        // Radio: всегда выбираем кликнутый. Сменить выбор = кликнуть
        // другой вариант (стандартное поведение radio-формы);
        // «снять выбор» отдельным кликом для radio не нужно.
        next[qi] = [label];
      }
      return next;
    });
  }

  function setText(qi: number, value: string) {
    setFreeText((prev) => {
      const next = [...prev];
      next[qi] = value;
      return next;
    });
  }

  const allAnswered = questions.every((q, i) => {
    const sel = selections[i] ?? [];
    const txt = (freeText[i] ?? '').trim();
    return sel.length > 0 || txt.length > 0;
  });

  function submit() {
    setSubmitting(true);
    const lines = questions.map((q, i) => {
      const sel = selections[i] ?? [];
      const txt = (freeText[i] ?? '').trim();
      const parts: string[] = [];
      if (sel.length > 0) parts.push(sel.join(', '));
      if (txt) parts.push(txt);
      return `Q: ${q.question}\nA: ${parts.join(' — ')}`;
    });
    onAnswer(lines.join('\n\n'));
  }

  return (
    <div className="rounded-2xl border border-amber-600/40 bg-amber-500/10 p-3 space-y-3">
      <div className="text-sm font-medium">❓ Вопрос от агента</div>
      {questions.map((q, qi) => (
        <div
          key={qi}
          className="rounded-lg border border-amber-500/30 bg-amber-950/10 p-3 space-y-2"
        >
          {q.header && (
            <div className="text-[11px] uppercase tracking-wide text-amber-300/70">
              {q.header}
            </div>
          )}
          <div className="text-[14px] font-medium text-amber-50">{q.question}</div>
          {q.options.length > 0 && (
            <ul className="space-y-1.5">
              {q.options.map((opt, oi) => {
                const checked = (selections[qi] ?? []).includes(opt.label);
                // Не используем нативные <input type="radio/checkbox"> +
                // controlled checked/onChange: в PWA-режиме на iOS и в
                // отдельных Chrome-сессиях onChange иногда не доходит до
                // React, и клик «не выбирает» вариант. <button> с aria
                // полностью под нашим контролем и работает везде.
                return (
                  <li key={oi}>
                    <button
                      type="button"
                      role={q.multiSelect ? 'checkbox' : 'radio'}
                      aria-checked={checked}
                      onClick={() => toggle(qi, opt.label)}
                      className={`flex w-full text-left gap-2 items-start rounded-md border p-2 cursor-pointer transition ${
                        checked
                          ? 'border-amber-400/60 bg-amber-500/15'
                          : 'border-amber-500/15 bg-black/20 hover:bg-amber-500/5 active:bg-amber-500/10'
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`mt-0.5 inline-block w-4 h-4 shrink-0 border ${
                          q.multiSelect ? 'rounded-sm' : 'rounded-full'
                        } ${
                          checked
                            ? 'border-amber-300 bg-amber-400'
                            : 'border-amber-400/50 bg-transparent'
                        }`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-medium text-amber-50">
                          {opt.label}
                        </span>
                        {opt.description && (
                          <span className="block text-[12px] text-amber-100/70 mt-0.5 leading-snug">
                            {opt.description}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <textarea
            value={freeText[qi] ?? ''}
            onChange={(e) => setText(qi, e.target.value)}
            placeholder={q.options.length > 0 ? 'или свой ответ…' : 'твой ответ…'}
            rows={2}
            className="w-full px-2 py-1.5 rounded-md bg-black/30 border border-amber-500/20 text-[13px] resize-none placeholder:text-amber-100/40"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="danger" className="flex-1" onClick={onDeny}>
          Отменить
        </Button>
        <Button
          className="flex-1"
          onClick={submit}
          disabled={!allAnswered || submitting}
        >
          Ответить
        </Button>
      </div>
    </div>
  );
}

function parseAskInput(toolInput: string): AskQuestion[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(toolInput);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || !('questions' in parsed)) return [];
  const raw = (parsed as { questions: unknown }).questions;
  if (!Array.isArray(raw)) return [];
  const out: AskQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== 'object') continue;
    const obj = q as Record<string, unknown>;
    const question = typeof obj.question === 'string' ? obj.question : '';
    if (!question) continue;
    const options: AskQuestion['options'] = [];
    if (Array.isArray(obj.options)) {
      for (const o of obj.options) {
        if (!o || typeof o !== 'object') continue;
        const oo = o as Record<string, unknown>;
        const label = typeof oo.label === 'string' ? oo.label : '';
        if (!label) continue;
        const description = typeof oo.description === 'string' ? oo.description : undefined;
        options.push(description ? { label, description } : { label });
      }
    }
    const item: AskQuestion = {
      question,
      options,
      multiSelect: obj.multiSelect === true,
    };
    if (typeof obj.header === 'string' && obj.header.length > 0) {
      item.header = obj.header;
    }
    out.push(item);
  }
  return out;
}
